/**
 * SchoolContext — resolve o tenant atual a partir do hostname.
 *
 * Lógica:
 *   1. Lê window.location.hostname (ex: "digitalpressure.monetizai.online")
 *   2. Extrai o slug (primeira parte antes do domínio raiz)
 *   3. Busca saas_organizations.slug == slug
 *   4. Provê para a app: school, branding dinâmico, isMainSite, isLoading
 *
 * Domínios canônicos:
 *   - monetizai.online (raiz / marketing)
 *   - *.monetizai.online (tenant slugs das escolas)
 *
 * Legacy ainda aceito (migração): *.12brain.org (será desligado em breve).
 *
 * Hosts reservados (não buscam tenant):
 *   - www.* → site principal
 *   - "vibecode" (legacy) → painel admin canônico no domínio antigo
 *   - localhost / 127.0.0.1 / 192.168.* → dev
 *
 * Fallback: se subdomain não bate com nenhuma org, exibe 404 amigável.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface School {
  id: string;
  slug: string;
  name: string;
  status: string;
  owner_user_id: string | null;
  metadata: Record<string, unknown> | null;
}

interface SchoolContextValue {
  school: School | null;
  isLoading: boolean;
  isMainSite: boolean;
  isAdminSite: boolean;
  notFound: boolean;
  hostname: string;
  slug: string | null;
  refresh: () => void;
}

const SchoolContext = createContext<SchoolContextValue | null>(null);

const RESERVED_SLUGS = new Set(["vibecode", "www", "admin", "api", "mail", "ftp"]);
// Domínio canônico atual + domínio legacy (mantido enquanto migrar). Qualquer
// um dos dois resolve da mesma forma — o nginx atende ambos.
const ROOT_DOMAIN = "monetizai.online";
const LEGACY_ROOT_DOMAINS = ["12brain.org"];

function parseHostname(hostname: string): { slug: string | null; isMainSite: boolean } {
  const lower = hostname.toLowerCase();

  // Dev hosts
  if (lower === "localhost" || lower === "127.0.0.1" || lower.startsWith("192.168.") || lower.startsWith("10.")) {
    return { slug: null, isMainSite: true };
  }

  const knownRoots = [ROOT_DOMAIN, ...LEGACY_ROOT_DOMAINS];

  // Apex domain (qualquer um dos roots conhecidos)
  if (knownRoots.includes(lower)) {
    return { slug: null, isMainSite: true };
  }

  // *.monetizai.online OU *.12brain.org → extrai slug
  for (const root of knownRoots) {
    if (lower.endsWith(`.${root}`)) {
      const prefix = lower.slice(0, -1 * (root.length + 1));
      // "vibecode" / "www" / "www.<algo>" → site principal
      if (RESERVED_SLUGS.has(prefix) || prefix.startsWith("www.")) {
        return { slug: null, isMainSite: true };
      }
      // Multi-level subdomain — usa o último segmento como slug
      const lastSegment = prefix.split(".").pop() || prefix;
      return { slug: lastSegment, isMainSite: false };
    }
  }

  // Domínio não gerenciado por nós (custom domain?) — por enquanto, main site
  return { slug: null, isMainSite: true };
}

export function SchoolProvider({ children }: { children: ReactNode }) {
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const { slug, isMainSite } = parseHostname(hostname);
  const isAdminSite = isMainSite && typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  const [school, setSchool] = useState<School | null>(null);
  const [isLoading, setIsLoading] = useState(!isMainSite && !!slug);
  const [notFound, setNotFound] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (isMainSite || !slug) {
      setIsLoading(false);
      setSchool(null);
      setNotFound(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);

    (async () => {
      try {
        const { data, error } = await supabase
          .from("saas_organizations")
          .select("id, slug, name, status, owner_user_id, metadata")
          .eq("slug", slug)
          .eq("status", "active")
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          console.error("[SchoolContext] query error:", error.message);
          setSchool(null);
          setNotFound(true);
        } else if (!data) {
          setSchool(null);
          setNotFound(true);
        } else {
          setSchool(data as School);
          setNotFound(false);
          // Persist em localStorage pra resolver rapido em reloads
          try {
            localStorage.setItem("activeSchool", JSON.stringify(data));
          } catch {}
        }
      } catch (e) {
        if (!cancelled) {
          console.error("[SchoolContext] unexpected:", e);
          setSchool(null);
          setNotFound(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, isMainSite, refreshKey]);

  const value: SchoolContextValue = {
    school,
    isLoading,
    isMainSite,
    isAdminSite,
    notFound,
    hostname,
    slug,
    refresh: () => setRefreshKey((k) => k + 1),
  };

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
}

export function useSchool(): SchoolContextValue {
  const ctx = useContext(SchoolContext);
  if (!ctx) {
    throw new Error("useSchool deve ser usado dentro de <SchoolProvider>");
  }
  return ctx;
}

/**
 * Helper para componentes precisarem do organization_id atual nas queries.
 * Retorna o id (string UUID) ou null se nao em contexto de tenant.
 */
export function useCurrentOrgId(): string | null {
  const { school } = useSchool();
  return school?.id ?? null;
}
